"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import SecondaryButton from "@/components/utils/button/SecondaryButton";
import SelectGroupModalSearchName from "@/components/utils/select-group/SelectGroupModalSearchName";
import { getWaveCurationsQueryKey } from "@/hooks/waves/useWaveCurations";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveCuration } from "@/generated/models/ApiWaveCuration";
import type { ApiWaveCurationRequest } from "@/generated/models/ApiWaveCurationRequest";
import type { ApiWaveMin } from "@/generated/models/ApiWaveMin";
import { getRandomColorWithSeed, getTimeAgo } from "@/helpers/Helpers";
import { ImageScale, getScaledImageUri } from "@/helpers/image.helpers";
import { commonApiFetch, commonApiPost } from "@/services/api/common-api";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import Image from "next/image";
import { useRef, useState, type ReactNode, type ChangeEvent } from "react";
const CURATION_NAME_PRESETS = ["Art", "Favourites", "Marketplace", "Important"] as const;
const getMatchingPresetLabel = (value: string | null | undefined): string | null => {
  const normalizedValue = value?.trim().toLowerCase();
  if (!normalizedValue) {
    return null;
  }
  const matchingPreset = CURATION_NAME_PRESETS.find(preset => preset.toLowerCase() === normalizedValue);
  return matchingPreset ?? null;
};
interface MyStreamWaveCurationCreateDialogProps {
  readonly wave: Pick<ApiWave, "id" | "wave"> | Pick<ApiWaveMin, "id" | "admin_group_id">;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSaved: (curation: ApiWaveCuration) => Promise<void> | void;
  readonly showSuccessToast?: boolean | undefined;
  readonly curation?: ApiWaveCuration | null | undefined;
  readonly initialGroup?: ApiGroupFull | null | undefined;
}
const getWaveAdminGroupId = (wave: Pick<ApiWave, "id" | "wave"> | Pick<ApiWaveMin, "id" | "admin_group_id">): string | null => {
  if ("wave" in wave) {
    return wave.wave.admin_group.group?.id ?? null;
  }
  return wave.admin_group_id ?? null;
};
const getGroupCreatorLabel = (group: ApiGroupFull): string => group.created_by.handle ? `@${group.created_by.handle}` : group.created_by.primary_address;
const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return fallback;
};
const getCurationSaveErrorMessage = ({
  error,
  fallbackErrorMessage,
  isEditMode
}: {
  readonly error: unknown;
  readonly fallbackErrorMessage: string;
  readonly isEditMode: boolean;
}): string => {
  const errorMessage = getErrorMessage(error, fallbackErrorMessage);
  const normalizedMessage = errorMessage.toLowerCase();
  if (normalizedMessage.includes("already exists") || normalizedMessage.includes("duplicate") || normalizedMessage.includes("unique constraint")) {
    return "A curation with this name already exists in this wave. Choose another name.";
  }
  if (normalizedMessage.includes("permission") || normalizedMessage.includes("forbidden") || normalizedMessage.includes("not authorized") || normalizedMessage.includes("not allowed")) {
    return isEditMode ? "You don't have permission to update this curation." : "You don't have permission to create a curation with the selected group.";
  }
  return errorMessage;
};
const getInitialSelectedGroupId = ({
  wave,
  curation,
  initialGroup,
  isEditMode
}: {
  readonly wave: Pick<ApiWave, "id" | "wave"> | Pick<ApiWaveMin, "id" | "admin_group_id">;
  readonly curation?: ApiWaveCuration | null | undefined;
  readonly initialGroup?: ApiGroupFull | null | undefined;
  readonly isEditMode: boolean;
}): string | null => {
  if (initialGroup?.id) {
    return initialGroup.id;
  }
  if (curation?.group_id) {
    return curation.group_id;
  }
  if (isEditMode) {
    return null;
  }
  return getWaveAdminGroupId(wave);
};
const getSubmitButtonLabel = (isPending: boolean, isEditMode: boolean): string => {
  if (isPending) {
    return isEditMode ? "Saving..." : "Creating...";
  }
  if (isEditMode) {
    return "Save";
  }
  return "Create";
};
function CurationGroupRow(t0) {
  const $ = _c(60);
  const {
    group,
    onSelect,
    trailingContent,
    isSelected: t1
  } = t0;
  const isSelected = t1 === undefined ? false : t1;
  const isInteractive = onSelect !== undefined;
  let t2;
  if ($[0] !== group.created_by.banner1_color || $[1] !== group.created_by.handle) {
    t2 = group.created_by.banner1_color ?? getRandomColorWithSeed(group.created_by.handle ?? "");
    $[0] = group.created_by.banner1_color;
    $[1] = group.created_by.handle;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const avatarAccentStart = t2;
  let t3;
  if ($[3] !== group.created_by.banner2_color || $[4] !== group.created_by.handle) {
    t3 = group.created_by.banner2_color ?? getRandomColorWithSeed(group.created_by.handle ?? "");
    $[3] = group.created_by.banner2_color;
    $[4] = group.created_by.handle;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const avatarAccentEnd = t3;
  let t4;
  if ($[6] !== group.created_at) {
    t4 = getTimeAgo(new Date(group.created_at).getTime());
    $[6] = group.created_at;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const timeAgo = t4;
  let t5;
  if ($[8] !== group) {
    t5 = getGroupCreatorLabel(group).charAt(0).toUpperCase();
    $[8] = group;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const avatarFallbackLabel = t5;
  const hasTrailingContent = trailingContent !== undefined && trailingContent !== null;
  const avatarAlt = group.created_by.handle ? `${group.created_by.handle} profile picture` : "Group creator profile picture";
  let t6;
  if ($[10] !== group || $[11] !== onSelect) {
    t6 = () => {
      if (onSelect === undefined) {
        return;
      }
      onSelect(group);
    };
    $[10] = group;
    $[11] = onSelect;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleSelect = t6;
  let rowClassName = "tw-border-white/[0.06] tw-bg-iron-900/40";
  if (isInteractive) {
    rowClassName = isSelected ? "tw-cursor-pointer tw-border-white/20 tw-bg-iron-900 tw-shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_10px_24px_rgba(0,0,0,0.28),0_0_18px_rgba(255,255,255,0.06)]" : "desktop-hover:hover:tw-border-white/12 tw-cursor-pointer tw-border-white/[0.06] tw-bg-iron-950/70 desktop-hover:hover:tw-bg-iron-900/70";
  }
  const selectionIndicatorClassName = isSelected ? "tw-border-iron-100 tw-bg-iron-100 tw-shadow-[0_0_10px_rgba(255,255,255,0.2)]" : "tw-border-white/10 tw-bg-transparent desktop-hover:group-hover:tw-border-white/30";
  const t7 = `linear-gradient(135deg, ${avatarAccentStart} 0%, ${avatarAccentEnd} 100%)`;
  let t8;
  if ($[13] !== t7) {
    t8 = {
      background: t7
    };
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== avatarAlt || $[16] !== avatarFallbackLabel || $[17] !== group.created_by.pfp) {
    t9 = group.created_by.pfp ? <Image src={getScaledImageUri(group.created_by.pfp, ImageScale.W_AUTO_H_50)} width={40} height={40} alt={avatarAlt} className="tw-h-full tw-w-full tw-object-cover" /> : <><svg viewBox="0 0 24 24" fill="none" className="tw-h-4 tw-w-4 tw-text-iron-50 tw-drop-shadow-md" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg><span className="tw-sr-only">{avatarFallbackLabel}</span></>;
    $[15] = avatarAlt;
    $[16] = avatarFallbackLabel;
    $[17] = group.created_by.pfp;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className="tw-relative tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-shadow-[inset_0_2px_4px_rgba(255,255,255,0.2),0_2px_8px_rgba(0,0,0,0.4)]" style={t8}>{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== group.name) {
    t11 = <p className="tw-mb-1 tw-truncate tw-text-sm tw-font-semibold tw-text-iron-50">{group.name}</p>;
    $[22] = group.name;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== group) {
    t12 = getGroupCreatorLabel(group);
    $[24] = group;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12) {
    t13 = <span className="tw-inline-block tw-max-w-[8rem] tw-truncate tw-font-medium tw-text-iron-400">{t12}</span>;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== timeAgo) {
    t14 = timeAgo && <><span className="tw-leading-none tw-text-iron-600">·</span><span className="tw-text-iron-500">Created {timeAgo}</span></>;
    $[28] = timeAgo;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t13 || $[31] !== t14) {
    t15 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5 tw-text-[11px]">{t13}{t14}</div>;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t11 || $[34] !== t15) {
    t16 = <div className="tw-min-w-0 tw-flex-1">{t11}{t15}</div>;
    $[33] = t11;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t10 || $[37] !== t16) {
    t17 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3">{t10}{t16}</div>;
    $[36] = t10;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== hasTrailingContent || $[40] !== trailingContent) {
    t18 = hasTrailingContent && <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2">{trailingContent}</div>;
    $[39] = hasTrailingContent;
    $[40] = trailingContent;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== hasTrailingContent || $[43] !== isSelected || $[44] !== selectionIndicatorClassName) {
    t19 = !hasTrailingContent && <div className={clsx("tw-flex tw-h-[18px] tw-w-[18px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition-all tw-duration-300", selectionIndicatorClassName)} aria-hidden="true">{isSelected && <svg viewBox="0 0 24 24" fill="none" className="tw-h-3 tw-w-3 tw-flex-shrink-0 tw-text-iron-950" stroke="currentColor" strokeWidth="3.5"><polyline points="20 6 9 17 4 12" /></svg>}</div>;
    $[42] = hasTrailingContent;
    $[43] = isSelected;
    $[44] = selectionIndicatorClassName;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t17 || $[47] !== t18 || $[48] !== t19) {
    t20 = <>{t17}{t18}{t19}</>;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const rowContent = t20;
  let rowClassNames;
  let t21;
  if ($[50] !== handleSelect || $[51] !== isInteractive || $[52] !== isSelected || $[53] !== rowClassName || $[54] !== rowContent) {
    t21 = Symbol.for("react.early_return_sentinel");
    bb0: {
      rowClassNames = clsx("tw-group tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-p-4 tw-transition-all", rowClassName);
      if (isInteractive) {
        t21 = <button type="button" onClick={handleSelect} aria-pressed={isSelected} className={clsx(rowClassNames, "tw-w-full tw-bg-transparent tw-text-left")}>{rowContent}</button>;
        break bb0;
      }
    }
    $[50] = handleSelect;
    $[51] = isInteractive;
    $[52] = isSelected;
    $[53] = rowClassName;
    $[54] = rowContent;
    $[55] = rowClassNames;
    $[56] = t21;
  } else {
    rowClassNames = $[55];
    t21 = $[56];
  }
  if (t21 !== Symbol.for("react.early_return_sentinel")) {
    return t21;
  }
  let t22;
  if ($[57] !== rowClassNames || $[58] !== rowContent) {
    t22 = <div className={rowClassNames}>{rowContent}</div>;
    $[57] = rowClassNames;
    $[58] = rowContent;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  return t22;
}
function CurationGroupSearchState(t0) {
  const $ = _c(29);
  const {
    canCloseGroupSearch,
    onCloseGroupSearch,
    shouldShowDefaultGroupError,
    groupSearchName,
    setGroupSearchName,
    isSearchingGroups,
    isSearchFiltered,
    searchedGroups,
    selectedGroupId,
    onGroupSelect
  } = t0;
  const loadingMessage = isSearchFiltered ? "Searching groups..." : "Loading groups...";
  let content;
  if (isSearchingGroups) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CircleLoader size={CircleLoaderSize.SMALL} />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== loadingMessage) {
      t2 = <div className="tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.06] tw-bg-iron-950/60 tw-px-4 tw-py-4"><div className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-iron-400">{t1}<span>{loadingMessage}</span></div></div>;
      $[1] = loadingMessage;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    content = t2;
  } else {
    if (searchedGroups.length > 0) {
      let t1;
      if ($[3] !== onGroupSelect || $[4] !== searchedGroups || $[5] !== selectedGroupId) {
        let t2;
        if ($[7] !== onGroupSelect || $[8] !== selectedGroupId) {
          t2 = group => <CurationGroupRow key={group.id} group={group} onSelect={onGroupSelect} isSelected={selectedGroupId === group.id} />;
          $[7] = onGroupSelect;
          $[8] = selectedGroupId;
          $[9] = t2;
        } else {
          t2 = $[9];
        }
        t1 = searchedGroups.map(t2);
        $[3] = onGroupSelect;
        $[4] = searchedGroups;
        $[5] = selectedGroupId;
        $[6] = t1;
      } else {
        t1 = $[6];
      }
      let t2;
      if ($[10] !== t1) {
        t2 = <div className="tw-grid tw-grid-cols-1 tw-gap-2">{t1}</div>;
        $[10] = t1;
        $[11] = t2;
      } else {
        t2 = $[11];
      }
      content = t2;
    } else {
      let t1;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <div className="tw-rounded-xl tw-border tw-border-dashed tw-border-white/[0.08] tw-bg-iron-950/60 tw-px-4 tw-py-5"><p className="tw-mb-0 tw-text-sm tw-text-iron-400">No groups found.</p></div>;
        $[12] = t1;
      } else {
        t1 = $[12];
      }
      content = t1;
    }
  }
  let t1;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-iron-300">Choose another group</p>;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] !== canCloseGroupSearch || $[15] !== onCloseGroupSearch) {
    t2 = canCloseGroupSearch && <button type="button" onClick={onCloseGroupSearch} className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-iron-400 tw-transition desktop-hover:hover:tw-text-iron-200">Back</button>;
    $[14] = canCloseGroupSearch;
    $[15] = onCloseGroupSearch;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== t2) {
    t3 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">{t1}{t2}</div>;
    $[17] = t2;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== shouldShowDefaultGroupError) {
    t4 = shouldShowDefaultGroupError && <div className="tw-rounded-xl tw-border tw-border-solid tw-border-rose-500/20 tw-bg-rose-500/10 tw-px-4 tw-py-4"><p className="tw-mb-0 tw-text-sm tw-text-rose-300">Failed to load the current curator group. Choose another group below.</p></div>;
    $[19] = shouldShowDefaultGroupError;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== groupSearchName || $[22] !== setGroupSearchName) {
    t5 = <SelectGroupModalSearchName filterName={groupSearchName} setFilterName={setGroupSearchName} autoFocus={true} />;
    $[21] = groupSearchName;
    $[22] = setGroupSearchName;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== content || $[25] !== t3 || $[26] !== t4 || $[27] !== t5) {
    t6 = <div className="tw-space-y-3">{t3}{t4}{t5}{content}</div>;
    $[24] = content;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
}
function CurationGroupSummaryState(t0) {
  const $ = _c(9);
  const {
    selectedGroup,
    isInitialGroupLoading,
    onOpenGroupSearch
  } = t0;
  if (selectedGroup === null && isInitialGroupLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.06] tw-bg-iron-950/70 tw-px-4 tw-py-4"><p className="tw-mb-0 tw-text-sm tw-text-iron-400">Loading selected group...</p></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (selectedGroup !== null) {
    let t1;
    if ($[1] !== onOpenGroupSearch) {
      t1 = <SecondaryButton onClicked={onOpenGroupSearch} size="sm" className="tw-whitespace-nowrap">Change group</SecondaryButton>;
      $[1] = onOpenGroupSearch;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== selectedGroup || $[4] !== t1) {
      t2 = <CurationGroupRow group={selectedGroup} trailingContent={t1} />;
      $[3] = selectedGroup;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="tw-mb-0 tw-text-sm tw-text-iron-400">No group selected yet.</p>;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== onOpenGroupSearch) {
    t2 = <div className="tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.06] tw-bg-iron-950/70 tw-px-4 tw-py-4"><div className="tw-flex tw-items-center tw-justify-between tw-gap-3">{t1}<SecondaryButton onClicked={onOpenGroupSearch} size="sm" className="tw-whitespace-nowrap">Choose group</SecondaryButton></div></div>;
    $[7] = onOpenGroupSearch;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
export default function MyStreamWaveCurationCreateDialog(t0) {
  const $ = _c(108);
  const {
    wave,
    isOpen,
    onClose,
    onSaved,
    showSuccessToast: t1,
    curation,
    initialGroup
  } = t0;
  const showSuccessToast = t1 === undefined ? true : t1;
  const queryClient = useQueryClient();
  const {
    requestAuth,
    setToast
  } = useAuth();
  const isEditMode = !!curation;
  const initialName = curation?.name ?? "";
  const dialogTitle = isEditMode ? "Edit curation" : "Create curation";
  const fallbackErrorMessage = isEditMode ? "Failed to update curation." : "Failed to create curation.";
  let t2;
  if ($[0] !== curation || $[1] !== initialGroup || $[2] !== isEditMode || $[3] !== wave) {
    t2 = getInitialSelectedGroupId({
      wave,
      curation,
      initialGroup,
      isEditMode
    });
    $[0] = curation;
    $[1] = initialGroup;
    $[2] = isEditMode;
    $[3] = wave;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const initialSelectedGroupId = t2;
  let t3;
  if ($[5] !== initialName) {
    t3 = () => initialName;
    $[5] = initialName;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const [name, setName] = useState(t3);
  const [selectedGroupOverride, setSelectedGroupOverride] = useState(initialGroup ?? null);
  let t4;
  if ($[7] !== initialGroup || $[8] !== initialSelectedGroupId) {
    t4 = () => !initialGroup && !initialSelectedGroupId;
    $[7] = initialGroup;
    $[8] = initialSelectedGroupId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const [isGroupSearchOpen, setIsGroupSearchOpen] = useState(t4);
  const [groupSearchName, setGroupSearchName] = useState(null);
  const nameInputRef = useRef(null);
  let t5;
  let t6;
  if ($[10] !== initialSelectedGroupId) {
    t5 = [QueryKey.GROUP, initialSelectedGroupId];
    t6 = async () => {
      if (!initialSelectedGroupId) {
        throw new Error("No default group selected.");
      }
      return await commonApiFetch({
        endpoint: `groups/${initialSelectedGroupId}`
      });
    };
    $[10] = initialSelectedGroupId;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  const t7 = !!initialSelectedGroupId && !initialGroup;
  let t8;
  if ($[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = {
      queryKey: t5,
      queryFn: t6,
      enabled: t7,
      staleTime: 300000
    };
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const {
    data: resolvedInitialGroup,
    isLoading: isInitialGroupLoading,
    isError: isInitialGroupError
  } = useQuery(t8);
  const selectedGroup = selectedGroupOverride ?? initialGroup ?? resolvedInitialGroup ?? null;
  const trimmedName = name.trim();
  let t9;
  if ($[17] !== groupSearchName) {
    t9 = groupSearchName?.trim() ?? "";
    $[17] = groupSearchName;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const trimmedGroupSearchName = t9;
  const isSearchFiltered = trimmedGroupSearchName.length > 0;
  let t10;
  if ($[19] !== name) {
    t10 = getMatchingPresetLabel(name);
    $[19] = name;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const selectedPresetLabel = t10;
  const isSubmitDisabled = selectedGroup === null || trimmedName.length === 0;
  const shouldShowDefaultGroupError = selectedGroup === null && !isInitialGroupLoading && isInitialGroupError;
  const canCloseGroupSearch = selectedGroup !== null;
  const isGroupSearchVisible = isGroupSearchOpen || shouldShowDefaultGroupError;
  let t11;
  if ($[21] !== isSearchFiltered || $[22] !== trimmedGroupSearchName) {
    t11 = isSearchFiltered ? {
      group_name: trimmedGroupSearchName
    } : undefined;
    $[21] = isSearchFiltered;
    $[22] = trimmedGroupSearchName;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const groupSearchParams = t11;
  let t12;
  if ($[24] !== trimmedGroupSearchName) {
    t12 = [QueryKey.GROUPS, {
      group_name: trimmedGroupSearchName
    }];
    $[24] = trimmedGroupSearchName;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== groupSearchParams) {
    t13 = async () => await commonApiFetch({
      endpoint: "groups",
      params: groupSearchParams
    });
    $[26] = groupSearchParams;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== isGroupSearchVisible || $[29] !== t12 || $[30] !== t13) {
    t14 = {
      queryKey: t12,
      queryFn: t13,
      enabled: isGroupSearchVisible,
      placeholderData: keepPreviousData
    };
    $[28] = isGroupSearchVisible;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const {
    data: t15,
    isFetching: isSearchingGroups
  } = useQuery(t14);
  let t16;
  if ($[32] !== t15) {
    t16 = t15 === undefined ? [] : t15;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const searchedGroups = t16;
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = group => {
      setSelectedGroupOverride(group);
      setGroupSearchName(null);
      setIsGroupSearchOpen(false);
    };
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const handleGroupSelect = t17;
  let t18;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setGroupSearchName(null);
      setIsGroupSearchOpen(true);
    };
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const handleOpenGroupSearch = t18;
  let t19;
  if ($[36] !== selectedGroup) {
    t19 = () => {
      if (selectedGroup === null) {
        return;
      }
      setGroupSearchName(null);
      setIsGroupSearchOpen(false);
    };
    $[36] = selectedGroup;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const handleCloseGroupSearch = t19;
  const handlePresetSelect = presetLabel => {
    setName(presetLabel);
    globalThis.window.requestAnimationFrame(() => {
      const input = nameInputRef.current;
      if (input === null) {
        return;
      }
      input.focus();
      const caretPosition = presetLabel.length;
      input.setSelectionRange(caretPosition, caretPosition);
    });
  };
  let t20;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = event => {
      setName(event.target.value);
    };
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const handleNameChange = t20;
  const handleSubmit = () => {
    if (selectedGroup === null || trimmedName.length === 0) {
      return;
    }
    saveMutation.mutate({
      name: trimmedName,
      group_id: selectedGroup.id
    });
  };
  let t21;
  if ($[39] !== curation || $[40] !== isEditMode || $[41] !== requestAuth || $[42] !== wave) {
    t21 = async body => {
      const auth = await requestAuth();
      if (!auth.success) {
        throw new Error("Authentication was cancelled.");
      }
      return await commonApiPost({
        endpoint: isEditMode ? `waves/${wave.id}/curations/${curation.id}` : `waves/${wave.id}/curations`,
        body,
        errorMode: "structured"
      });
    };
    $[39] = curation;
    $[40] = isEditMode;
    $[41] = requestAuth;
    $[42] = wave;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== isEditMode || $[45] !== onClose || $[46] !== onSaved || $[47] !== queryClient || $[48] !== setToast || $[49] !== showSuccessToast || $[50] !== wave) {
    t22 = async saved => {
      queryClient.setQueryData(getWaveCurationsQueryKey(wave.id), current => {
        if (!current) {
          return isEditMode ? current : [saved];
        }
        const hasSavedCuration = current.some(item => item.id === saved.id);
        if (!hasSavedCuration) {
          return [...current, saved];
        }
        return current.map(item_0 => item_0.id === saved.id ? saved : item_0);
      });
      await queryClient.invalidateQueries({
        queryKey: getWaveCurationsQueryKey(wave.id)
      });
      if (showSuccessToast) {
        setToast({
          type: "success",
          message: isEditMode ? "Curation updated." : "Curation created."
        });
      }
      await onSaved(saved);
      onClose();
    };
    $[44] = isEditMode;
    $[45] = onClose;
    $[46] = onSaved;
    $[47] = queryClient;
    $[48] = setToast;
    $[49] = showSuccessToast;
    $[50] = wave;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== fallbackErrorMessage || $[53] !== isEditMode || $[54] !== setToast) {
    t23 = error => {
      setToast({
        type: "error",
        message: getCurationSaveErrorMessage({
          error,
          fallbackErrorMessage,
          isEditMode
        })
      });
    };
    $[52] = fallbackErrorMessage;
    $[53] = isEditMode;
    $[54] = setToast;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t21 || $[57] !== t22 || $[58] !== t23) {
    t24 = {
      mutationFn: t21,
      onSuccess: t22,
      onError: t23
    };
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  const saveMutation = useMutation(t24);
  const submitButtonLabel = getSubmitButtonLabel(saveMutation.isPending, isEditMode);
  let groupPickerContent;
  if (isGroupSearchVisible) {
    const t25 = selectedGroup?.id ?? null;
    let t26;
    if ($[60] !== canCloseGroupSearch || $[61] !== groupSearchName || $[62] !== handleCloseGroupSearch || $[63] !== isSearchFiltered || $[64] !== isSearchingGroups || $[65] !== searchedGroups || $[66] !== shouldShowDefaultGroupError || $[67] !== t25) {
      t26 = <CurationGroupSearchState canCloseGroupSearch={canCloseGroupSearch} onCloseGroupSearch={handleCloseGroupSearch} shouldShowDefaultGroupError={shouldShowDefaultGroupError} groupSearchName={groupSearchName} setGroupSearchName={setGroupSearchName} isSearchingGroups={isSearchingGroups} isSearchFiltered={isSearchFiltered} searchedGroups={searchedGroups} selectedGroupId={t25} onGroupSelect={handleGroupSelect} />;
      $[60] = canCloseGroupSearch;
      $[61] = groupSearchName;
      $[62] = handleCloseGroupSearch;
      $[63] = isSearchFiltered;
      $[64] = isSearchingGroups;
      $[65] = searchedGroups;
      $[66] = shouldShowDefaultGroupError;
      $[67] = t25;
      $[68] = t26;
    } else {
      t26 = $[68];
    }
    groupPickerContent = t26;
  } else {
    let t25;
    if ($[69] !== isInitialGroupLoading || $[70] !== selectedGroup) {
      t25 = <CurationGroupSummaryState selectedGroup={selectedGroup} isInitialGroupLoading={isInitialGroupLoading} onOpenGroupSearch={handleOpenGroupSearch} />;
      $[69] = isInitialGroupLoading;
      $[70] = selectedGroup;
      $[71] = t25;
    } else {
      t25 = $[71];
    }
    groupPickerContent = t25;
  }
  const T0 = MobileWrapperDialog;
  const t25 = true;
  const t26 = true;
  const t27 = true;
  const t28 = "md:tw-max-w-lg";
  const t29 = "tw-mb-0 tw-border-b tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-white/[0.06] tw-pb-4 tw-pt-6";
  const t30 = "tw-flex tw-min-h-0 tw-flex-1 tw-flex-col";
  const t31 = "tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300";
  const t32 = "tw-flex tw-flex-col tw-gap-8 tw-px-4 tw-py-6 sm:tw-px-6";
  const t33 = "tw-space-y-4";
  let t34;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <label htmlFor="curation-name" className="tw-block tw-text-sm tw-font-medium tw-text-iron-300">Name</label>;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  const t35 = "tw-flex tw-flex-wrap tw-gap-2.5";
  const t36 = CURATION_NAME_PRESETS.map(preset => {
    const isSelected = selectedPresetLabel === preset;
    return <button key={preset} type="button" onClick={() => handlePresetSelect(preset)} aria-pressed={isSelected} className={clsx("tw-inline-flex tw-appearance-none tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-shadow-sm tw-transition-all tw-duration-300 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white/20", isSelected ? "tw-border-white/20 tw-bg-iron-800 tw-text-iron-100" : "tw-border-white/10 tw-bg-iron-900 tw-text-iron-300 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-50")}>{preset}</button>;
  });
  let t37;
  if ($[73] !== t36) {
    t37 = <div className={t35}>{t36}</div>;
    $[73] = t36;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== handleNameChange || $[76] !== name || $[77] !== nameInputRef) {
    t38 = <div className="tw-relative tw-w-full sm:tw-max-w-[260px]"><input ref={nameInputRef} id="curation-name" type="text" value={name} onChange={handleNameChange} autoComplete="off" placeholder="Create your own custom..." className="tw-form-input tw-block tw-w-full tw-appearance-none tw-rounded-lg tw-border-0 tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-text-iron-100 tw-caret-primary-400 tw-shadow-inner tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out placeholder:tw-text-sm placeholder:tw-text-iron-500 hover:tw-ring-iron-650 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400" /></div>;
    $[75] = handleNameChange;
    $[76] = name;
    $[77] = nameInputRef;
    $[78] = t38;
  } else {
    t38 = $[78];
  }
  let t39;
  if ($[79] !== t34 || $[80] !== t37 || $[81] !== t38) {
    t39 = <div className={t33}>{t34}{t37}{t38}</div>;
    $[79] = t34;
    $[80] = t37;
    $[81] = t38;
    $[82] = t39;
  } else {
    t39 = $[82];
  }
  let t40;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <span className="tw-block tw-text-sm tw-font-medium tw-text-iron-300">Who can curate?</span>;
    $[83] = t40;
  } else {
    t40 = $[83];
  }
  let t41;
  if ($[84] !== groupPickerContent) {
    t41 = <div className="tw-space-y-4">{t40}<div className="tw-space-y-3.5">{groupPickerContent}</div></div>;
    $[84] = groupPickerContent;
    $[85] = t41;
  } else {
    t41 = $[85];
  }
  let t42;
  if ($[86] !== t39 || $[87] !== t41) {
    t42 = <div className={t31}><div className={t32}>{t39}{t41}</div></div>;
    $[86] = t39;
    $[87] = t41;
    $[88] = t42;
  } else {
    t42 = $[88];
  }
  let t43;
  if ($[89] !== onClose) {
    t43 = <SecondaryButton onClicked={onClose} className="tw-border-white/[0.06] tw-bg-transparent tw-text-iron-400 tw-ring-0 desktop-hover:hover:tw-border-white/[0.06] desktop-hover:hover:tw-bg-white/[0.06] desktop-hover:hover:tw-text-iron-100">Cancel</SecondaryButton>;
    $[89] = onClose;
    $[90] = t43;
  } else {
    t43 = $[90];
  }
  let t44;
  if ($[91] !== handleSubmit || $[92] !== isSubmitDisabled || $[93] !== saveMutation.isPending || $[94] !== submitButtonLabel) {
    t44 = <PrimaryButton loading={saveMutation.isPending} disabled={isSubmitDisabled} onClicked={handleSubmit} padding="tw-px-6 tw-py-2.5">{submitButtonLabel}</PrimaryButton>;
    $[91] = handleSubmit;
    $[92] = isSubmitDisabled;
    $[93] = saveMutation.isPending;
    $[94] = submitButtonLabel;
    $[95] = t44;
  } else {
    t44 = $[95];
  }
  let t45;
  if ($[96] !== t43 || $[97] !== t44) {
    t45 = <div className="tw-flex-shrink-0 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/[0.06] tw-bg-iron-950 tw-px-5 tw-py-5 sm:tw-px-6"><div className="tw-flex tw-justify-end tw-gap-3">{t43}{t44}</div></div>;
    $[96] = t43;
    $[97] = t44;
    $[98] = t45;
  } else {
    t45 = $[98];
  }
  let t46;
  if ($[99] !== t42 || $[100] !== t45) {
    t46 = <div className={t30}>{t42}{t45}</div>;
    $[99] = t42;
    $[100] = t45;
    $[101] = t46;
  } else {
    t46 = $[101];
  }
  let t47;
  if ($[102] !== T0 || $[103] !== dialogTitle || $[104] !== isOpen || $[105] !== onClose || $[106] !== t46) {
    t47 = <T0 title={dialogTitle} isOpen={isOpen} onClose={onClose} noPadding={t25} tabletModal={t26} tall={t27} maxWidthClass={t28} headerClassName={t29}>{t46}</T0>;
    $[102] = T0;
    $[103] = dialogTitle;
    $[104] = isOpen;
    $[105] = onClose;
    $[106] = t46;
    $[107] = t47;
  } else {
    t47 = $[107];
  }
  return t47;
}